import { useState, type ReactElement } from 'react';
import classNames from 'classnames';
import NavbarVerticalMenuItem from './NavbarVerticalMenuItem';
import { Collapse, Nav, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import { useAppContext } from '@/Main';
import { useSelector } from 'react-redux';
import { routeToHandler } from '@/helpers/utils';

type NavbarRoute = {
  name: string;
  to?: string;
  exact?: boolean;
  active?: boolean;
  disabled?: boolean;
  divider?: boolean;
  target?: string;
  action?: string;
  defaultOpen?: boolean;
  badge?: { type: string; text: string };
  newFeatureKey?: string;
  children?: NavbarRoute[];
};

type NavbarRootState = {
  selectedDomain: { name: string; slug?: string };
};

type CollapseItemsProps = {
  route: NavbarRoute;
  onRouteAction?: (action: string) => void;
  newFeatureKey?: string;
  onRouteVisited?: (featureKey: string) => void;
  isFeatureUnread?: (featureKey: string) => boolean;
  itemClassName?: string;
};

const ComingSoonTooltip = ({
  id,
  children,
}: {
  id: string;
  children: ReactElement;
}) => (
  <OverlayTrigger
    placement="left"
    popperConfig={{ strategy: 'fixed' }}
    overlay={
      <Tooltip id={id} style={{ position: 'fixed' }}>
        به زودی
      </Tooltip>
    }
  >
    {children}
  </OverlayTrigger>
);

const CollapseItems = ({
  route,
  onRouteAction,
  newFeatureKey,
  onRouteVisited,
  isFeatureUnread,
  itemClassName
}: CollapseItemsProps) => {
  const { pathname } = useLocation();

  const openCollapse = (childrens: NavbarRoute[]) => {
    const checkLink = (children: NavbarRoute): boolean => {
      if (
        children.to &&
        children.to !== '#!' &&
        (pathname === children.to || pathname.includes(children.to))
      ) {
        return true;
      }
      return (
        Object.prototype.hasOwnProperty.call(children, 'children') &&
        (children.children?.some(checkLink) ?? false)
      );
    };
    return childrens.some(checkLink);
  };

  const [open, setOpen] = useState(
    typeof route.defaultOpen === 'boolean'
      ? route.defaultOpen
      : openCollapse(route.children ?? [])
  );

  const toggleLink = (
    <Nav.Link
      onClick={() => {
        if (route.disabled) return;
        if (newFeatureKey) onRouteVisited?.(newFeatureKey);
        setOpen(!open);
      }}
      className={classNames('dropdown-indicator cursor-pointer', {
        'text-500': !route.active,
        'disabled-link nav-link-coming-soon': route.disabled,
      })}
      aria-expanded={open}
    >
      <NavbarVerticalMenuItem
        iconActive={openCollapse(route.children ?? [])}
        route={{
          ...route,
          showNewIndicator:
            !!route.newFeatureKey &&
            isFeatureUnread?.(route.newFeatureKey)
        }}
      />
    </Nav.Link>
  );

  return (
    <Nav.Item as="li" className={itemClassName}>
      {route.disabled ? (
        <ComingSoonTooltip id={`nav-soon-${route.name}`}>
          <span className="d-block navbar-coming-soon-trigger">{toggleLink}</span>
        </ComingSoonTooltip>
      ) : (
        toggleLink
      )}
      {!route.disabled && (
        <Collapse in={open}>
          <Nav className="flex-column nav ps-2" as="ul">
            <NavbarVerticalMenu
              routes={route.children ?? []}
              onRouteAction={onRouteAction}
              newFeatureKey={newFeatureKey}
              onRouteVisited={onRouteVisited}
              isFeatureUnread={isFeatureUnread}
            />
          </Nav>
        </Collapse>
      )}
    </Nav.Item>
  );
};

type NavbarVerticalMenuProps = {
  routes: NavbarRoute[];
  onRouteAction?: (action: string) => void;
  newFeatureKey?: string;
  onRouteVisited?: (featureKey: string) => void;
  isFeatureUnread?: (featureKey: string) => boolean;
  itemClassName?: string;
};

const NavbarVerticalMenu = ({
  routes,
  onRouteAction,
  newFeatureKey,
  onRouteVisited,
  isFeatureUnread,
  itemClassName
}: NavbarVerticalMenuProps) => {
  const { pathname } = useLocation();
  const { slug, domain, domain_slug, loc, lang, keyword } = useParams();
  const selectedDomain = useSelector(
    (state: NavbarRootState) => state.selectedDomain
  );

  const {
    config: { showBurgerMenu },
    setConfig
  } = useAppContext();

  const handleNavItemClick = () => {
    if (showBurgerMenu) {
      setConfig('showBurgerMenu', !showBurgerMenu);
    }
  };

  return routes.map((route, index) => {
    // A disabled item can intentionally reuse a destination, so the URL alone
    // is not a unique identity for sibling menu items.
    const routeKey = `${route.to || 'group'}-${route.name}-${index}`;
    const routeFeatureKey = route.newFeatureKey || newFeatureKey;
    if (route.divider) {
      return (
        <Nav.Item as="li" key={routeKey} role="separator">
          <hr className="my-2 mx-3 navbar-vertical-divider" />
        </Nav.Item>
      );
    }
    if (!route.children) {
      const link = (
        <NavLink
          end={route.exact}
          to={
            route.disabled
              ? '#!'
              : routeToHandler(
                  route,
                  selectedDomain,
                  domain,
                  domain_slug,
                  slug,
                  loc,
                  lang,
                  keyword
                ) || '#!'
          }
          target={route.target || undefined}
          state={{ path: `${pathname}`, query: null }}
          onClick={event => {
            if (route.disabled) {
              event.preventDefault();
              return;
            }
            if (routeFeatureKey) onRouteVisited?.(routeFeatureKey);
            if (route.action) {
              event.preventDefault();
              onRouteAction?.(route.action);
              return;
            }
            if (route.name === 'Modal') {
              setConfig('openAuthModal', true);
            }
          }}
          className={({ isActive }) =>
            classNames(
              route.disabled
                ? 'nav-link disabled-link nav-link-coming-soon'
                : isActive && route.to !== '#!'
                  ? route.to?.includes('http')
                    ? 'nav-link'
                    : 'active nav-link'
                  : 'nav-link',
              'min-w-0'
            )
          }
        >
          {({ isActive }) => (
            <NavbarVerticalMenuItem
              iconActive={
                !route.disabled &&
                isActive &&
                route.to !== '#!' &&
                !route.to?.includes('http')
              }
              route={{
                ...route,
                showNewIndicator:
                  !!route.newFeatureKey &&
                  isFeatureUnread?.(route.newFeatureKey)
              }}
            />
          )}
        </NavLink>
      );

      return (
        <Nav.Item
          as="li"
          key={routeKey}
          className={itemClassName}
          onClick={handleNavItemClick}
        >
          {route.disabled && route.name !== 'سابقه ای یافت نشد' ? (
            <ComingSoonTooltip id={`nav-soon-${routeKey}`}>
              <span className="d-block navbar-coming-soon-trigger">{link}</span>
            </ComingSoonTooltip>
          ) : (
            link
          )}
        </Nav.Item>
      );
    }
    return (
      <CollapseItems
        route={route}
        key={routeKey}
        onRouteAction={onRouteAction}
        newFeatureKey={routeFeatureKey}
        onRouteVisited={onRouteVisited}
        isFeatureUnread={isFeatureUnread}
        itemClassName={itemClassName}
      />
    );
  });
};

export default NavbarVerticalMenu;
